import { FC, Fragment, MouseEvent, useCallback, useState } from "react";
import {
  Menu,
  MenuItem,
  OutlinedInput,
  PopoverOrigin,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { DateLabelNullable } from "@/services/cloudchipr.api";

interface NormalizedCostWidgetDateRangeSelectProps {
  dateLabel: DateLabelNullable;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  onChange(dateLabel: DateLabelNullable): void;
  CustomTriggerComponent?: FC<{ onClick: (event: any) => void }>;
}

export const NormalizedCostWidgetDateRangeSelect: FC<
  NormalizedCostWidgetDateRangeSelectProps
> = ({
  dateLabel,
  onChange,
  anchorOrigin,
  transformOrigin,
  CustomTriggerComponent,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = useCallback(
    (newDateLabel: DateLabelNullable) => {
      if (newDateLabel === dateLabel) {
        return;
      }

      onChange(newDateLabel);
      handleClose();
    },
    [dateLabel, onChange],
  );

  const getHandleSelect = useCallback(
    (newDateLabel: DateLabelNullable) => {
      return () => handleSelect(newDateLabel);
    },
    [handleSelect],
  );

  const changeHandler = useCallback(
    (e: SelectChangeEvent<DateLabelNullable>) => {
      const label = e.target.value;

      if (!label) {
        return;
      }

      onChange(label as DateLabelNullable);
    },
    [onChange],
  );

  if (CustomTriggerComponent) {
    return (
      <Fragment>
        <CustomTriggerComponent onClick={handleOpen} />

        <Menu
          open={menuOpen}
          anchorEl={anchorEl}
          onClose={handleClose}
          onChange={changeHandler as any}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
        >
          <MenuItem
            value="last_three_months"
            onClick={getHandleSelect("last_three_months")}
          >
            Last 3 months
          </MenuItem>
          <MenuItem
            value="last_six_months"
            onClick={getHandleSelect("last_six_months")}
          >
            {" "}
            Last 6 months
          </MenuItem>
          <MenuItem
            value="last_twelve_months"
            onClick={getHandleSelect("last_twelve_months")}
          >
            Last 12 months
          </MenuItem>
        </Menu>
      </Fragment>
    );
  }

  return (
    <Select
      fullWidth
      size="small"
      value={dateLabel}
      input={
        <OutlinedInput
          startAdornment={
            <InputAdornment position="start">
              <CalendarTodayIcon fontSize="small" />
            </InputAdornment>
          }
        />
      }
      onChange={changeHandler}
    >
      <MenuItem value="last_three_months">Last 3 months</MenuItem>
      <MenuItem value="last_six_months">Last 6 months</MenuItem>
      <MenuItem value="last_twelve_months">Last 12 months</MenuItem>
    </Select>
  );
};
